import type { TourConfig, Hotspot, ImageQualityMode, SceneImageMeta, SceneImageSourceKind } from '../types'
import { getOptimizedThumbnailUrl } from '../utils/imageTransforms'
import { sceneAdjustmentThumbnailUrl } from '../../../shared/src/utils/sceneAdjustments'

// API Service v2 — uses public /viewer/:id endpoint (no auth required)
interface ApiServiceConfig {
  apiUrl?: string
  storageUrl?: string
}

const DEFAULT_AUTO_ROTATE_SPEED = 0.2
const AUTO_ROTATE_SPEED_PRESETS: Record<string, number> = {
  low: 0.2,
  medium: 2,
  med: 2,
  fast: 5,
  high: 5,
}
const DEFAULT_SCENE_TRANSITION = { type: 'fade', duration: 450 } as const

function normalizeAutoRotateSpeed(value: unknown, fallback = DEFAULT_AUTO_ROTATE_SPEED): number {
  if (typeof value === 'string') {
    const preset = AUTO_ROTATE_SPEED_PRESETS[value.toLowerCase()]
    if (preset !== undefined) return preset
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return Math.max(0.05, Math.min(parsed, 10))
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0.05, Math.min(value, 10))
  }
  return fallback
}

function normalizeBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'off'].includes(normalized)) return false
  }
  return fallback
}

function normalizeSceneTransition(value: unknown): { type: 'fade' | 'none'; duration: number } {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
  const type = source.type === 'none' || value === 'none' ? 'none' : 'fade'
  const durationValue = Number(source.duration ?? source.ms ?? source.speed)
  const duration = Number.isFinite(durationValue)
    ? Math.max(0, Math.min(Math.round(durationValue), 1200))
    : DEFAULT_SCENE_TRANSITION.duration

  return {
    type,
    duration: type === 'none' ? 0 : duration,
  }
}

function normalizeImageQualityMode(value: unknown): ImageQualityMode {
  return value === 'fast' || value === 'best' ? value : 'auto'
}

function normalizeImageMeta(value: unknown, imageUrl: string): SceneImageMeta | null {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, any>
    : null
  if (!source && !imageUrl) return null

  const variants = source?.variants && typeof source.variants === 'object' && !Array.isArray(source.variants)
    ? { ...source.variants }
    : null

  return {
    source_url: source?.source_url || null,
    master_url: source?.master_url || imageUrl || null,
    width: Number.isFinite(Number(source?.width)) ? Number(source?.width) : null,
    height: Number.isFinite(Number(source?.height)) ? Number(source?.height) : null,
    content_type: source?.content_type || null,
    byte_size: Number.isFinite(Number(source?.byte_size)) ? Number(source?.byte_size) : null,
    variants,
    source_kind: (['upload', 'ai', 'ai-ultra', 'legacy'].includes(source?.source_kind)
      ? source?.source_kind
      : 'legacy') as SceneImageSourceKind,
  }
}

/**
 * Clean a scene name for display:
 * - Strips common image file extensions (.jpg, .jpeg, .png, .webp, .tif, .tiff)
 * - Returns null if the result is purely numeric (e.g. "001", "1") — avoids
 *   showing raw auto-generated filenames like "001.jpg" as scene labels.
 */
function cleanSceneName(name: string | null | undefined): string | null {
  if (!name) return null
  const stripped = name.replace(/\.(jpe?g|png|webp|tiff?)$/i, '').trim()
  if (!stripped || /^\d+$/.test(stripped)) return null
  return stripped
}

/**
 * Normalize a hotspot from various legacy/modern formats to the viewer Hotspot interface.
 * Handles:
 *   - Builder format:  position: {x, y, z}   (3-D world coords, sphere radius ~500)
 *   - Legacy flat:     {yaw, pitch}           (top-level fields)
 *   - Already correct: position: {pitch, yaw}
 */
function normalizeHotspot(hs: any, index: number): Hotspot {
  let position: Hotspot['position']

  if (hs.position && typeof hs.position === 'object') {
    if ('x' in hs.position && 'y' in hs.position && 'z' in hs.position) {
      // Builder format: preserve {x, y, z} for advanced placement rendering.
      position = {
        x: Number(hs.position.x),
        y: Number(hs.position.y),
        z: Number(hs.position.z),
      }
    } else {
      // Already {pitch, yaw} (legacy or already-normalized)
      position = {
        pitch: Number(hs.position.pitch ?? 0),
        yaw:   Number(hs.position.yaw   ?? 0),
      }
    }
  } else {
    // Flat legacy fields
    position = {
      yaw:   Number(hs.yaw   ?? 0),
      pitch: Number(hs.pitch ?? 0),
    }
  }

  const rawStyle = hs.style && typeof hs.style === 'object' ? hs.style : {}
  const placement = ['sphere', '2d', 'floor', 'wall'].includes(hs.placement) ? hs.placement : undefined

  return {
    id: hs.id || `hotspot-${index}`,
    type: hs.type || 'info',
    title: hs.title ?? hs.name ?? '',
    position,
    placement,
    scaleOnZoom: Boolean(hs.scaleOnZoom),
    style: {
      ...rawStyle,
      ...(rawStyle.color ? { color: String(rawStyle.color) } : {}),
      ...(rawStyle.size !== undefined ? { size: Number(rawStyle.size) } : {}),
      ...(rawStyle.opacity !== undefined ? { opacity: Number(rawStyle.opacity) } : {}),
      ...(rawStyle.rotationDeg !== undefined ? { rotationDeg: Number(rawStyle.rotationDeg) } : {}),
      ...(rawStyle.labelOnHover !== undefined ? { labelOnHover: Boolean(rawStyle.labelOnHover) } : {}),
    },
    icon: hs.icon || hs.style?.icon || 'eye',
    content: hs.content || hs.popoverContent || '',
    contentHtml: hs.contentHtml ?? hs.popoverHtml ?? true,
    targetSceneId: hs.targetSceneId || null,
    url: hs.url || hs.link || null,
    urlNewTab: hs.urlNewTab ?? true,
  }
}

export class ApiService {
  private apiUrl: string
  private storageUrl: string

  constructor(config: ApiServiceConfig = {}) {
    this.apiUrl = config.apiUrl || import.meta.env.VITE_API_URL || 'https://api-dev.virtualtoureasy.com'
    this.storageUrl = config.storageUrl || import.meta.env.VITE_STORAGE_URL || 'https://storage-dev.virtualtoureasy.com'
  }

  /**
   * Fetch tour configuration from the public viewer endpoint.
   * Supports both UUID format and legacy orgId/tourId format.
   */
  async getTourConfig(tourId: string, previewToken?: string | null): Promise<TourConfig> {
    try {
      // Determine if this is a legacy format (contains /)
      let endpoint = tourId.includes('/')
        ? `${this.apiUrl}/viewer/legacy/${tourId}`
        : `${this.apiUrl}/viewer/${tourId}`

      // Append preview token when present so the API bypasses publish/visibility checks
      if (previewToken) {
        endpoint += `?preview_token=${encodeURIComponent(previewToken)}`
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Tour not found')
        }
        throw new Error('Failed to load tour configuration')
      }

      const config = await response.json()

      // Check if tour is unpublished
      if (config.unpublished) {
        return { ...config, unpublished: true } as any
      }

      // Check if tour is password-protected
      if (config.password_required) {
        return { ...config, password_required: true } as any
      }

      const storageBaseUrl = config.storage_base_url || this.storageUrl

      return this.normalizeTourConfig(config, storageBaseUrl)
    } catch (error) {
      console.error('Error fetching tour config:', error)
      throw error
    }
  }

  /**
   * Verify tour password and get full tour config
   */
  async verifyTourPassword(tourId: string, password: string): Promise<TourConfig> {
    const endpoint = `${this.apiUrl}/viewer/${tourId}/verify`
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ password }),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Incorrect password')
      }
      throw new Error('Failed to verify password')
    }

    const config = await response.json()
    const storageBaseUrl = config.storage_base_url || this.storageUrl

    return this.normalizeTourConfig(config, storageBaseUrl)
  }

  private normalizeTourConfig(config: any, storageBaseUrl: string): TourConfig {
    // Transform scenes with proper storage URLs
    if (config.scenes && Array.isArray(config.scenes)) {
      config.scenes = config.scenes.map((scene: any) => this.normalizeScene(scene, storageBaseUrl))
    }

    const scenes = Array.isArray(config.scenes) ? config.scenes : []
    const firstSceneId = scenes[0]?.id || scenes[0]?.uuid || null
    const tourConfig = config.tour_config || {}
    const settings = tourConfig.settings || {}
    const viewerConfig = config.viewer_config || {}
    const autoRotate = normalizeBoolean(viewerConfig.autoRotate ?? settings.autoRotate, false)
    const autoRotateSpeed = normalizeAutoRotateSpeed(
      viewerConfig.autoRotateSpeed ?? settings.autoRotateSpeed ?? settings.rotationSpeed
    )
    const sceneTransition = normalizeSceneTransition(
      viewerConfig.sceneTransition ??
      settings.sceneTransition ??
      {
        type: settings.sceneTransitionType,
        duration: settings.sceneTransitionDuration ?? settings.defaultTransitionDuration,
      }
    )
    const autoplay = normalizeBoolean(viewerConfig.autoplay ?? settings.autoplay, false)
    const template = viewerConfig.template ?? settings.template
    const configuredStartScene = config.startScene || tourConfig.startScene || tourConfig.defaultScene || tourConfig.sceneId || null
    const sceneIds = new Set(scenes.map((scene: any) => scene.id || scene.uuid).filter(Boolean))
    const startScene = configuredStartScene && sceneIds.has(configuredStartScene)
      ? configuredStartScene
      : firstSceneId
    const compass = normalizeBoolean(
      viewerConfig.compass ??
      viewerConfig.showCompass ??
      settings.compass ??
      settings.showCompass,
      false
    )

    const videos = Array.isArray(config.videos)
      ? config.videos.filter((video: any) => video && typeof video.url === 'string' && /^https?:\/\//.test(video.url))
      : []

    return {
      ...config,
      videos,
      startScene: startScene || undefined,
      viewer_config: {
        ...viewerConfig,
        autoRotate,
        autoRotateSpeed,
        sceneTransition,
        autoplay,
        ...(template ? { template } : {}),
        compass,
        showCompass: compass,
        imageQualityMode: normalizeImageQualityMode(viewerConfig.imageQualityMode ?? settings.imageQualityMode),
      },
    }
  }

  /**
   * Resolve a URL - if it's already absolute, return as-is.
   * Otherwise prepend the storage base URL.
   */
  private resolveUrl(path: string | null | undefined, baseUrl: string): string {
    if (!path) return ''
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }
    return `${baseUrl}/${path.replace(/^\//, '')}`
  }

  private normalizeScene(scene: any, storageBaseUrl: string) {
    const image = this.resolveUrl(scene.image_url || scene.image, storageBaseUrl)
    const thumbnailSource = sceneAdjustmentThumbnailUrl(scene.meta) || scene.thumbnail_url || scene.thumbnail
    const thumbnail = thumbnailSource
      ? this.resolveUrl(thumbnailSource, storageBaseUrl)
      : image
        ? getOptimizedThumbnailUrl(image)
        : undefined

    return {
      ...scene,
      id: scene.uuid || scene.id,
      title: cleanSceneName(scene.name) || scene.title || '',
      description: scene.description || null,
      image,
      thumbnail,
      image_meta: normalizeImageMeta(scene.image_meta, image),
      hotspots: (Array.isArray(scene.hotspots) ? scene.hotspots : []).map((hs: any, i: number) => normalizeHotspot(hs, i)),
      audio: scene.audio_url ? {
        url: this.resolveUrl(scene.audio_url, storageBaseUrl),
        volume: scene.audio_volume ?? 1.0,
        autoplay: !!scene.audio_autoplay,
        loop: scene.audio_loop !== false,
      } : undefined,
      initialView: {
        yaw: scene.initial_yaw || 0,
        pitch: scene.initial_pitch || 0,
        zoom: scene.initial_zoom || 100,
      },
      compassNorthOffset: scene.compass_north_offset || 0,
    }
  }
}
